import React from 'react';

// Decorative background shared by the sign-in and two-step verification
// pages. The parent must be `position: relative; overflow: hidden` and sit
// its content at z-index >= 2 (the dots/quote/tagline use z-index 1).
const AuthBackground = () => (
  <>
    <style>{`
      /* Large curved blue field bleeding off the right edge, approximating
         the wave-shaped two-tone background (light left / blue right).
         Given a slow, subtle breathing motion so the page feels alive
         rather than a static screenshot. */
      .login-bg-shape {
        position: absolute;
        top: 50%;
        left: 44%;
        width: 95vmax;
        height: 95vmax;
        border-radius: 50%;
        transform: translateY(-50%);
        transform-origin: center;
        background: linear-gradient(150deg, #5b9bf7 0%, #2f6fe0 45%, #1642b0 100%);
        z-index: 0;
        animation: login-bg-breathe 22s ease-in-out infinite;
      }
      @keyframes login-bg-breathe {
        0%, 100% { transform: translateY(-50%) scale(1) rotate(0deg); }
        50% { transform: translateY(-50%) scale(1.035) rotate(1.2deg); }
      }

      /* Soft ambient bokeh drifting slowly behind the card for depth. */
      .login-float {
        position: absolute;
        border-radius: 50%;
        background: radial-gradient(circle, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 70%);
        pointer-events: none;
        z-index: 0;
      }
      .login-float-1 { width: 130px; height: 130px; top: 20%; right: 32%; animation: login-float-drift-1 15s ease-in-out infinite; }
      .login-float-2 { width: 80px; height: 80px; bottom: 24%; right: 58%; animation: login-float-drift-2 19s ease-in-out infinite; }
      @keyframes login-float-drift-1 {
        0%, 100% { transform: translate(0, 0); }
        50% { transform: translate(-20px, 24px); }
      }
      @keyframes login-float-drift-2 {
        0%, 100% { transform: translate(0, 0); }
        50% { transform: translate(16px, -20px); }
      }
      .login-bg-highlight {
        position: absolute;
        top: 42%;
        left: 46%;
        width: 42vmax;
        height: 42vmax;
        border-radius: 50%;
        transform: translate(-15%, -50%);
        background: radial-gradient(circle, rgba(219,234,254,0.6) 0%, rgba(219,234,254,0) 70%);
        z-index: 0;
        pointer-events: none;
      }
      .login-bg-corner {
        position: absolute;
        bottom: -70px;
        left: -70px;
        width: 180px;
        height: 180px;
        border-radius: 50%;
        border: 1px solid rgba(99,132,199,0.3);
        background: rgba(219,234,254,0.35);
        z-index: 0;
        pointer-events: none;
      }

      /* Decorative dot-grid corner */
      .login-dots {
        position: absolute;
        width: 150px;
        height: 150px;
        background-image: radial-gradient(rgba(255,255,255,0.6) 1.5px, transparent 1.5px);
        background-size: 15px 15px;
        pointer-events: none;
        z-index: 1;
      }
      .login-dots-top {
        top: 2.5rem;
        right: 3rem;
        animation: login-dots-pulse 4s ease-in-out infinite;
      }
      @keyframes login-dots-pulse {
        0%, 100% { opacity: 0.55; }
        50% { opacity: 1; }
      }

      /* Handwritten accent text */
      .login-quote {
        position: absolute;
        font-family: 'Caveat', cursive;
        font-weight: 700;
        line-height: 1.2;
        pointer-events: none;
        z-index: 1;
      }
      .login-quote-top {
        top: 1.75rem;
        right: 2rem;
        font-size: 1.5rem;
        color: rgba(255,255,255,0.9);
        text-align: right;
      }

      /* Bottom-right footer tagline */
      .login-footer-tag {
        position: absolute;
        bottom: 2rem;
        right: 3rem;
        z-index: 1;
        font-size: 0.8rem;
        font-weight: 600;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: rgba(255,255,255,0.85);
        text-align: right;
        pointer-events: none;
      }
      .login-footer-tag-line {
        display: block;
        width: 24px;
        height: 2px;
        background: rgba(255,255,255,0.65);
        margin: 0 0 0.5rem auto;
      }

      @media (prefers-reduced-motion: reduce) {
        .login-bg-shape, .login-float-1, .login-float-2, .login-dots-top {
          animation: none;
        }
      }
      @media (max-width: 900px) {
        .login-dots, .login-quote, .login-footer-tag {
          display: none;
        }
      }
      /* On short screens the (taller) OTP card reaches the top-right corner. */
      @media (max-height: 760px) {
        .login-dots, .login-quote {
          display: none;
        }
      }
    `}</style>

    <div className="login-bg-shape" />
    <div className="login-bg-highlight" />
    <div className="login-bg-corner" />
    <div className="login-float login-float-1" />
    <div className="login-float login-float-2" />
    <div className="login-dots login-dots-top" />
    <div className="login-quote login-quote-top">"Smarter Tools<br />Brighter Business"</div>
    <div className="login-footer-tag">
      <span className="login-footer-tag-line" />
      People &nbsp;|&nbsp; Process &nbsp;|&nbsp; Progress
    </div>
  </>
);

export default AuthBackground;
